import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { ArrowRight, Inbox, Loader2, Mail, Star } from "lucide-react";
import { db } from "@/firebase/config";

export default function AdminHome() {
  const [submissions, setSubmissions] = useState([]);
  const [orderClicks, setOrderClicks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [submissionSnap, clickSnap] = await Promise.all([
          getDocs(query(collection(db, "formSubmissions"), orderBy("createdAt", "desc"))),
          getDocs(query(collection(db, "orderClicks"), orderBy("clickedAt", "desc"))),
        ]);
        setSubmissions(submissionSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setOrderClicks(clickSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const stats = useMemo(() => {
    const orderingToday = submissions.filter((item) => item.interestLevel === "Ordering today").length;
    const couponOrders = submissions.filter((item) => item.couponCode).length;
    const coloured = submissions.filter((item) => item.plannerPreference === "Coloured").length;
    const blackWhite = submissions.filter((item) => item.plannerPreference === "Black & white").length;
    return { total: submissions.length, orderClicks: orderClicks.length, orderingToday, couponOrders, coloured, blackWhite };
  }, [orderClicks.length, submissions]);

  if (loading) {
    return (
      <div className="retro-card p-8 flex items-center gap-3" data-testid="admin-home-loading">
        <Loader2 className="animate-spin" />
        <span className="font-pixel text-[10px]">LOADING RESPONSES...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-home">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="font-pixel text-[10px] mb-1">// ADMIN - ORDERS</p>
          <h2 className="font-heading text-3xl uppercase">Public Form Responses</h2>
          <p className="font-body text-sm text-retro-ink/70 mt-1">
            This admin dashboard tracks order responses submitted from the public website.
          </p>
        </div>
        <Link to="/admin/submissions" className="retro-btn bg-retro-purple text-white px-4 py-3 inline-flex items-center gap-2 w-fit">
          View all responses <ArrowRight size={16} />
        </Link>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <ResponseStat testId="admin-stat-submissions" icon={Inbox} label="Total responses" value={stats.total} color="bg-retro-yellow" />
        <ResponseStat testId="admin-stat-order-clicks" icon={Star} label="Buy clicks" value={stats.orderClicks} color="bg-retro-pink" />
        <ResponseStat testId="admin-stat-coupon-orders" icon={Mail} label="Coupon used" value={stats.couponOrders} sub="Any coupon code" color="bg-retro-mint" />
        <ResponseStat testId="admin-stat-coloured" icon={Inbox} label="Coloured" value={stats.coloured} sub="Rs. 499 submissions" color="bg-retro-blue" />
        <ResponseStat testId="admin-stat-black-white" icon={Inbox} label="Black & white" value={stats.blackWhite} sub="Rs. 399 submissions" color="bg-white" />
      </div>

      <div className="retro-card p-0 overflow-hidden bg-white">
        <div className="border-b-[3px] border-retro-ink bg-retro-yellow px-5 py-3">
          <h3 className="font-heading text-xl uppercase">Latest Responses</h3>
        </div>
        <div className="divide-y divide-retro-ink/15">
          {submissions.slice(0, 5).map((item) => (
            <div key={item.id} className="grid gap-2 px-5 py-4 md:grid-cols-[1fr_1fr_auto] md:items-center">
              <div>
                <p className="font-heading uppercase">{item.name || "Unnamed"}</p>
                <p className="font-body text-sm text-retro-ink/65">{item.email || "-"}</p>
              </div>
              <div className="font-body text-sm">
                <p>{item.plannerPreference || item.interestType || "-"} - {item.interestLevel || "-"}</p>
                <p className="text-xs text-retro-ink/60">Which college: {item.college || "-"}</p>
              </div>
              <p className="font-body text-xs text-retro-ink/60">{formatDate(item.createdAt)}</p>
            </div>
          ))}
          {submissions.length === 0 && (
            <div className="px-5 py-8 text-center font-body text-sm text-retro-ink/70">
              No public responses submitted yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ResponseStat({ icon: Icon, label, value, sub, color, testId }) {
  return (
    <div className={`retro-card p-5 ${color}`} data-testid={testId}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-pixel text-[9px] uppercase mb-2">{label}</p>
          <p className="font-heading text-4xl leading-none">{value}</p>
          {sub && <p className="font-body text-xs mt-2 text-retro-ink/70">{sub}</p>}
        </div>
        <Icon size={26} className="shrink-0" />
      </div>
    </div>
  );
}

function formatDate(value) {
  if (!value) return "-";
  const date = value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}
